import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { useApi } from "@/core/api";
import { useGetBusinessUsers } from "@/user/query";
import { SpiffRule } from "./spiff-rule";
import {
  EnrichedPotentialSpiffTrigger,
  PotentialSpiffTrigger,
} from "./spiff-rule-trigger";
import { getSpiffTriggerWithUser } from "./utils";

const latestSpiffRulesKey = (businessId?: string | null) =>
  ["latest-spiff-rules", businessId] as const;
const activeSpiffRulesKey = (businessId?: string | null) =>
  ["active-spiff-rules", businessId] as const;
export const potentialTriggersKey = (
  businessId?: string | null,
  spiffRuleId?: string | null,
  searchTerm?: string,
) =>
  [
    "spiff-rule-potential-triggers",
    businessId,
    spiffRuleId,
    searchTerm,
  ] as const;

export const useGetLatestSpiffRules = (businessId?: string | null) => {
  const { get } = useApi();

  return useQuery({
    queryKey: latestSpiffRulesKey(businessId),
    queryFn: async () => {
      const params = new URLSearchParams({ latest: "true" });

      return get(`businesses/${businessId}/spiff_rules`, { params }).then(
        (res) => res.data as SpiffRule[],
      );
    },
    enabled: !!businessId,
  });
};

export const useGetActiveSpiffRules = (businessId?: string | null) => {
  const { get } = useApi();

  return useQuery({
    queryKey: activeSpiffRulesKey(businessId),
    queryFn: async () => {
      const params = new URLSearchParams({ active: "true" });

      return get(`businesses/${businessId}/spiff_rules`, { params }).then(
        (res) => res.data as SpiffRule[],
      );
    },
    enabled: !!businessId,
  });
};

export const useUpsertSpiffRule = (businessId?: string | null) => {
  const { post } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: Partial<SpiffRule>) =>
      // Unlike other "upsert" mutations, this one will always POST; the back-end
      // is responsible for determining whether to create or update.
      post(`businesses/${businessId}/spiff_rules`, params),
    onSuccess: () => {
      // Invalidate both queries to refetch current state from server
      queryClient.invalidateQueries({
        queryKey: latestSpiffRulesKey(businessId),
      });
      queryClient.invalidateQueries({
        queryKey: activeSpiffRulesKey(businessId),
      });
    },
  });
};

export const useDeleteSpiffRule = (businessId?: string | null) => {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (spiffId: string) =>
      api.delete(`businesses/${businessId}/spiff_rules/${spiffId}`),
    onSuccess: () => {
      // Invalidate both queries to refetch current state from server
      queryClient.invalidateQueries({
        queryKey: latestSpiffRulesKey(businessId),
      });
      queryClient.invalidateQueries({
        queryKey: activeSpiffRulesKey(businessId),
      });
    },
  });
};

export const useSearchTriggers = (
  businessId?: string | null,
  spiffRuleId?: string | null,
  searchTerm?: string,
) => {
  const { get } = useApi();
  const { data: users, isLoading: isLoadingUsers } =
    useGetBusinessUsers(businessId);

  return useQuery({
    queryKey: potentialTriggersKey(businessId, spiffRuleId, searchTerm),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (searchTerm) {
        params.set("search_term", searchTerm);
      } else {
        params.set("limit", "25");
      }
      if (spiffRuleId) {
        params.set("spiff_rule_id", spiffRuleId);
      }
      return get(`businesses/${businessId}/spiff_rule_potential_triggers`, {
        params,
      }).then(
        (res) =>
          res.data.map((t: PotentialSpiffTrigger) =>
            getSpiffTriggerWithUser(t, users),
          ) as EnrichedPotentialSpiffTrigger[],
      );
    },
    enabled: !!businessId && !!spiffRuleId && !isLoadingUsers,
    placeholderData: (previousData) => previousData,
  });
};
